import { useLayoutEffect, useRef } from 'react';
import { renderMarkdownToHtml } from '../../utils/markdown';
import { stripDisabledFormBlocks } from '../../utils/form-renderer';
import { renderChart } from '../../utils/chart-renderer';
import { isAutoScrollEnabled, scrollToBottom } from '../../utils/scroll';

export function MessageText({
  isUser,
  displayContent,
  content,
  isStreaming,
  onRef,
}: {
  isUser: boolean;
  displayContent: string;
  content: string;
  isStreaming?: boolean;
  onRef: (el: HTMLElement | null) => void;
}) {
  const innerRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
    if (isUser) return;
    const el = innerRef.current;
    if (!el) return;

    const scrollContainer = el.closest('.panel-body') as HTMLElement | null;
    const shouldStickToBottom = isAutoScrollEnabled(scrollContainer);

    const savedForms = new Map<string, { html: string; classList: string[] }>();
    el.querySelectorAll('.form-container.form-rendered').forEach((fc) => {
      const spec = (fc as HTMLElement).getAttribute('data-form-spec');
      if (spec) {
        savedForms.set(spec, {
          html: (fc as HTMLElement).innerHTML,
          classList: Array.from(fc.classList),
        });
      }
    });

      const preservedCharts: HTMLElement[] = Array.from(
      el.querySelectorAll('.chart-container.chart-rendered'),
    ) as HTMLElement[];

    el.innerHTML = renderMarkdownToHtml(stripDisabledFormBlocks(content), !!isStreaming);

    if (savedForms.size > 0) {
      el.querySelectorAll('.form-container').forEach((fc) => {
        const spec = (fc as HTMLElement).getAttribute('data-form-spec');
        if (spec && savedForms.has(spec)) {
          const saved = savedForms.get(spec)!;
          (fc as HTMLElement).innerHTML = saved.html;
          saved.classList.forEach((cls) => fc.classList.add(cls));
        }
      });
    }

    if (preservedCharts.length > 0) {
      const freshChartsBySpec = new Map<string, HTMLElement[]>();
      el.querySelectorAll('.chart-container').forEach((cc) => {
        const spec = (cc as HTMLElement).getAttribute('data-chart-spec');
        if (!spec) return;
        const list = freshChartsBySpec.get(spec) ?? [];
        list.push(cc as HTMLElement);
        freshChartsBySpec.set(spec, list);
      });

      for (const liveNode of preservedCharts) {
        const spec = liveNode.getAttribute('data-chart-spec');
        const candidates = spec ? freshChartsBySpec.get(spec) : undefined;
        const fresh = candidates?.shift(); // claim+consume the next unmatched same-spec placeholder
        if (fresh) fresh.replaceWith(liveNode);
      }
    }

    el.querySelectorAll('.chart-container:not(.chart-rendered):not(.chart-error)')
      .forEach((container) => renderChart(container as HTMLElement));

    if (shouldStickToBottom) {
      scrollToBottom(scrollContainer);
    }
  }, [content, isStreaming, isUser]);

  if (isUser) {
    if (!displayContent.trim()) return null;
    return (
      <span
        ref={onRef as (el: HTMLSpanElement | null) => void}
        className="message-text"
      >
        {displayContent}
      </span>
    );
  }
  return (
    <div
      ref={(el) => {
        innerRef.current = el;
        onRef(el);
      }}
      className="message-text md-content"
    />
  );
}
